import _ from 'lodash';
import moment from 'moment';

import { parseSearchParam } from '../hooks/useSearchParamsState';
import { formatAmountInCurrency, getPrettyPriceDisplay } from './formatting';
import {
  formatDate,
  getAgentDecisionSummary,
  getAgentSummary,
  getLambdaSummary,
  getReviewTagLabel,
  isTrue,
  readable
} from './url_data_agent_evaluations_helpers';

/* ────────────────────────────────────────────────────────────────
 * Live matches (agent-applied lane) helpers
 * ─────────────────────────────────────────────────────────────── */

export const APPLY_SKIP_REASON_COPY = {
  action_create_product: 'No existing product found',
  action_dismiss: 'Product status or page evidence needs review',
  action_escalate: 'Could not choose a reliable match',
  action_no_usable_match: 'No reliable match found',
  nonproduct_evidence_unverified: 'The source does not establish a non-product page',
  source_changed: 'The source changed after investigation',
  sold_listing_without_stored_page: 'The listing was sold when APM read it, but no stored copy of the page could confirm that before dismissing',
  verdict_not_attach: 'No existing product match',
  missing_product_id: 'The agent did not select a catalog product',
  incomplete_run: 'APM could not finish checking this link',
  source_blocked: 'Bot protection blocked the page, so APM could not read it',
  execution_retries_exhausted: 'APM could not complete the run after retrying automatically; see the retained errors in Details',
  creation_incomplete_run: 'product creation could not be verified',
  creation_needs_review: 'the new product needs human review',
  creation_search_incomplete: 'catalog searches did not complete',
  creation_identity_incomplete: 'the product name, brand or category needs review',
  creation_brand_conflict: 'The proposed maker conflicts with an existing catalog brand; verify its identity before creating the product',
  catalog_brand_attribution_unverified: "APM could not verify which catalog brand this shop's product label belongs to",
  creation_image_needs_review: 'the product image needs review',
  creation_possible_duplicate: 'A catalog product of the same brand may already be this product',
  creation_source_missing: 'the source product link is missing',
  creation_listing_sold_out: 'the resale listing was sold out when APM read it',
  creation_same_product_confirmed: 'APM judged an existing catalog product to be the same core item in another size, set or material',
  creation_preowned_channel: 'catalog processing would mark this new item as pre-owned',
  creation_awaiting_requester: 'Waiting for the catalog team member who confirmed this new product to create it',
  creation_url_listed_by_product: 'A catalog product already lists this link',
  below_threshold: 'Confidence is below the auto-match minimum',
  evidence_needs_review: 'The run could not complete reliable identity checks',
  visual_comparison_failed: 'The photo comparison did not finish, so APM could not confirm the match',
  product_hidden: 'The selected catalog product is hidden',
  catalog_product_needs_review: 'The selected catalog product needs grooming',
  duplicate_product: 'A possible catalog duplicate needs review',
  duplicate_product_unresolved: 'APM investigated the existing product but could not resolve the creation conflict',
  url_taken_by_product: 'Another product took this URL while the run was in flight',
  brand_possible_duplicate: 'An existing catalog brand may already be this maker; check it before creating a new brand',
  invalid_product: 'The proposed product is missing required catalog fields',
  proposal_changed: 'The product proposal changed during verification',
  url_stem_conflict: 'the URL-stem match conflicted with the page brand',
  preowned_item_on_new_goods_product: 'a pre-owned item matched a product that is not a pre-owned listing',
  resale_listing_on_other_listing_product: "a resale listing matched another listing's product",
  retailer_code_conflict: "the page and the matched product's listing carry different product codes from this retailer",
  gender_conflict: "the page is a men's item and the matched product is a women's product",
  fit_conflict: "the page is a Petite or Tall fit and the matched product's title doesn't name it",
  duplicate_colorway_records: 'the catalog has this product under more than one colourway record',
  source_redirected_to_homepage: "the link now opens the shop's front page",
  preowned_source: 'the page sells pre-owned goods',
  source_condition_unverified: "the page's pre-owned claim could not be verified against the source",
  vinted_listing: "the catalog doesn't take Vinted listings",
  reserved_by_human: 'Already being reviewed by the catalog team',
  urldata_no_longer_eligible: 'Link was already processed',
  superseded_by_human: 'Link was already processed',
  sent_to_human: 'Reviewer sent this link to the catalog team',
  corrected_by_reviewer: 'a reviewer matched it to a different product',
  proposal_expired: 'Approval window ended',
  lane_paused: 'auto-apply was paused',
  released_by_pause: 'auto-apply was paused',
  review_first: 'auto-apply was set to Review first',
  run_undone: 'an admin undid its detach run',
  agent_user_missing: 'the agent user is missing',
  apply_error: 'the apply errored'
};

export const getApplySkipReasonCopy = reason => APPLY_SKIP_REASON_COPY[reason] || readable(reason || 'unknown');

export const getApplyDecisionSummary = (row, control) => {
  if (isRunningRecovery(row)) {
    return { key: 'recovering', label: 'Investigating automatically' };
  }
  if (row?.status === 'RECOVERY_PENDING' && row.applyDecision === 'proposed') {
    return { key: 'recovering', label: control?.mode === 'paused' ? 'Automatic investigation paused' :
      row.applySkipReason === 'execution_retry_pending' ? 'Automatic retry queued' : 'Automatic investigation queued' };
  }
  // Older applied corrections carry their reason only on the review.
  if (row?.applySkipReason === 'corrected_by_reviewer' || (row?.revertedAt && row?.latestReview?.correct_Product_id &&
    !_.includes(row.latestReview.tags, 'human_match_audit'))) {
    return { key: 'corrected', label: 'Corrected' };
  }
  if (row?.revertedAt) return { key: 'sent_back', label: 'Sent back' };
  if (row?.applyDecision === 'applied')
    return isAutoApplied(row)
      ? { key: 'auto', label: isNonProductDismissal(row) ? 'Auto-dismissed' : isProductCreated(row) ? 'Auto-created' : 'Auto-matched' }
      : { key: 'applied', label: isNonProductDismissal(row) ? 'Dismissed by approval' : isProductCreated(row) ? 'Created and matched' : 'Approved match' };
  if (row?.applyDecision === 'proposed') {
    if (isRunningProposal(row)) return { key: 'checking', label: 'APM is checking this link' };
    if (getLiveMatchReviewReason(row)) return { key: 'proposed', label: isProductCreation(row) ? 'New product needs review' : 'Needs APM review' };
    return { key: 'proposed', label: getExcludedResaleDismissal(row)?.label ?? (isNonProductDismissal(row) ? 'Suggested non-product dismissal' : isProductCreation(row) ? 'Suggested new product' : 'Suggested') };
  }
  if (row?.humanAction === 'new_product') return { key: 'created', label: 'Created by team' };
  if (['superseded_by_human', 'urldata_no_longer_eligible'].includes(row?.applySkipReason)) return { key: 'already_resolved', label: 'Already processed' };
  if (row?.applySkipReason === 'sent_to_human') return { key: 'sent_back', label: 'Sent back' };
  if (row?.applySkipReason === 'proposal_expired') return { key: 'expired', label: 'Expired' };
  return { key: 'passed', label: 'Sent to catalog team' };
};

export const LIVE_ACTIVITY_STATE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'applied', label: 'Approved actions' },
  { value: 'auto', label: 'Completed automatically' },
  { value: 'passed', label: 'Sent to catalog team' },
  { value: 'created', label: 'Created by team' },
  { value: 'already_resolved', label: 'Already processed' },
  { value: 'sent_back', label: 'Sent back' },
  { value: 'corrected', label: 'Corrected' },
  { value: 'expired', label: 'Expired' }
];

export const getLiveMatchHandoffReason = row => {
  if (row?.applyDecision === 'proposed' || (row?.applyDecision === 'applied' && !row?.revertedAt)) return null;
  if (row?.applySkipReason) return getApplySkipReasonCopy(row.applySkipReason);
  if (row?.revertedAt) return getApplyDecisionSummary(row).key === 'corrected' ? 'Reviewer selected a different product' : 'Reviewer undid the match';
  if (row?.status && row.status !== 'SUCCEEDED') return 'APM could not finish checking this link';
  if ((row?.agentMatchVerdict || row?.matchVerdict) === 'new_product') return 'No existing product found';
  if ((row?.agentMatchVerdict || row?.matchVerdict) === 'dismiss') return 'Product status or page evidence needs review';
  return 'Could not choose a reliable match';
};

export const getLiveMatchReviewReason = row => {
  if (row?.applyDecision !== 'proposed' || row.applyAction || isRunningProposal(row)) return null;
  const reason = row.applyBlockReason || row.applySkipReason;
  return reason ? getApplySkipReasonCopy(reason) : row.applyAction === null ? 'Choose a catalog product or send this link for more investigation' : null;
};

const isRunningRecovery = row => row?.status === 'PROCESSING' && row.applyDecision == null &&
  !row.appliedAt && !row.revertedAt && !row.humanResolvedAt && !row.reviewer_User_id &&
  ['execution_retry_pending', 'duplicate_product_recovery'].includes(row.applySkipReason);

const isRunningProposal = row => row?.applyDecision === 'proposed' && ['PENDING', 'RECOVERY_PENDING', 'PROCESSING'].includes(row.status);

// Explain open holds without changing which actions the server allows.
export const getLiveMatchAutomationReason = (row, control) => {
  if (isRunningRecovery(row)) {
    return row.applySkipReason === 'execution_retry_pending' ? 'APM is retrying the failed run' : 'APM is investigating the conflicting catalog product';
  }
  if (row?.applyDecision !== 'proposed') return null;
  if (row.executionRetriesExhausted === true || row.agentRunJson?.executionRecovery?.exhausted) return getApplySkipReasonCopy('execution_retries_exhausted');
  if (row.status === 'RECOVERY_PENDING') {
    if (control?.mode === 'paused') return 'Automatic investigation is paused; it will wait until APM resumes';
    if (row.applySkipReason !== 'execution_retry_pending') return 'Waiting for a worker to investigate the conflicting catalog product';
    const retryAt = row.executionRetryAt ?? row.agentRunJson?.executionRecovery?.retryAt;
    if (!retryAt || !moment(retryAt).isValid()) return 'Automatic retry is waiting for a valid schedule';
    return moment(retryAt).isAfter() ? `Automatic retry scheduled ${moment(retryAt).fromNow()}` : 'Automatic retry is due; waiting for a worker';
  }
  if (isRunningProposal(row)) return null;
  if (row.status === 'TIMED_OUT') return 'The run timed out before producing a usable decision';
  if (row.status === 'FAILED') return 'The run failed before producing a usable decision';
  if (row.agentMatchError || row.agentRunJson?.agentResults?.match?.decision?.error) return 'The matching step failed; see Details';
  const policy = row.currentApplyPolicy;
  if (!policy) return getLiveMatchReviewReason(row) || 'APM is checking whether this proposal can be applied';
  if (control && !isSameApplyControl(control, policy)) return 'Refreshing the matching decision';
  if (policy.automaticHoldReason === 'review_first') return 'Review first is enabled; automatic matching is on hold';
  if (policy.automaticHoldReason === 'lane_paused') return 'Automatic matching is paused';
  if (policy.automaticHoldReason === 'below_threshold') {
    const isCreation = policy.action === 'create_product';
    // Repeats getApplyThresholdForAction in packages/server/src/pin_match/apply_mode.js, which admin can't import; change both together.
    const threshold = isCreation ? policy.minCreateConfidence ?? policy.minConfidence : policy.minConfidence;
    if (threshold == null) return 'Sampled auto-match is disabled; approval is required';
    if (policy.confidence != null) {
      return `${formatConfidence(policy.confidence)} confidence is below the current ${formatConfidence(threshold)} ${isCreation ? 'create threshold' : 'auto-match minimum'}`;
    }
  }
  return policy.automaticHoldReason ? getApplySkipReasonCopy(policy.automaticHoldReason) : isNonProductDismissal(row) ? 'Queued for automatic dismissal' : 'Queued for automatic matching';
};

export const isSameApplyControl = (a, b) => a.mode === b.mode && a.minConfidence === b.minConfidence && a.minCreateConfidence === b.minCreateConfidence;

// Retained checks explain a hold even after a reviewer sends it to the catalog team.
export const getLiveMatchUnresolvedChecks = row => {
  const run = row?.agentRunJson;
  if (row?.applyDecision === 'applied' && !row.revertedAt) return [];
  const reasons = [];
  const failures = run?.executionRecovery?.resolvedAt ? [] : run?.executionRecovery?.failures || [];
  for (const [index, failure] of failures.entries()) {
    reasons.push(`Execution failure ${index + 1}: ${failure.error || failure.status}`);
  }
  if (row?.statusMsg && ['FAILED', 'TIMED_OUT'].includes(row.status)) reasons.push(row.statusMsg);
  if (!run) return reasons;
  const agents = run.agentResults || {};
  const match = agents.match?.decision || {};
  for (const [name, agent] of Object.entries(agents)) {
    const decision = agent?.decision || {};
    if (decision.error || decision.degraded) reasons.push(`${readable(name)} check did not complete reliably${typeof decision.error === 'string' ? `: ${decision.error}` : ''}`);
    else if (decision.escalationRequested && (name !== 'image' || isProductCreation(row) || run.tier === 'ESCALATION')) {
      const reason = agent.reasoning || decision.reasoning;
      reasons.push(`${readable(name)}: ${reason || 'Human review requested'}`);
    }
  }
  const missing = run.riskMetadata?.evidenceQuality?.identityMissingFields || [];
  if (missing.length) reasons.push(`Source evidence is missing: ${missing.map(readable).join(', ')}`);
  const brand = agents.brand?.decision;
  if (isProductCreation(row) && row.applyAction !== 'create_product' && !row.createdAllBrandId && brand?.brandName?.trim() && (!Number.isInteger(brand.AllBrand_id) || brand.AllBrand_id <= 0)) {
    reasons.push(`Brand “${brand.brandName.trim()}” has not been linked to a catalog brand`);
  }
  if (['comparison_failed', 'no_comparison'].includes(match.visionGate?.action)) reasons.push('The visual comparison did not complete; product identity remains unverified');
  if (run.possibleDuplicateBrand?.AllBrand_id) reasons.push(`Catalog brand “${run.possibleDuplicateBrand.name}” (#${run.possibleDuplicateBrand.AllBrand_id}) may already be this maker`);
  if (match.unprovenCreateFloor?.Product_id) reasons.push(`Catalog product #${match.unprovenCreateFloor.Product_id} has strong identity evidence and has not been ruled out as a duplicate`);
  const uncompared = match.candidateComparisonCoverage?.uncomparedProductIds || [];
  if (uncompared.length) reasons.push(`Catalog comparisons are missing for ${uncompared.map(id => `#${id}`).join(', ')}`);
  for (const [name, status] of Object.entries(run.riskMetadata?.retrieval?.armStatus || {})) {
    if (String(status).startsWith('error:')) reasons.push(`${readable(name)} catalog search failed: ${String(status).slice(6)}`);
  }
  return _.uniq(reasons);
};

// Offer approval only when the server has a complete action for the proposal.
export const getLiveMatchActions = row => {
  const { key } = getApplyDecisionSummary(row);
  if (key === 'proposed') {
    return [
      ...(!getLiveMatchReviewReason(row) ? [{ key: 'approve', display: getExcludedResaleDismissal(row)?.action ?? (isNonProductDismissal(row) ? 'Dismiss non-product page' : isCatalogBrandCreation(row) ? 'Create brand, product and match' : isProductCreation(row) ? 'Create product and match' : 'Approve'), primary: true }] : []),
      { key: 'correct', display: getLiveMatchReviewReason(row) ? 'Choose product…' : 'Different…' },
      { key: 'send_back', display: 'Send to catalog team' }
    ];
  }
  if (key === 'applied' || key === 'auto') {
    return [
      { key: 'correct', display: 'Different…' },
      { key: 'undo', display: 'Undo' },
      ...(isProductCreationProcessingPending(row) ? [{ key: 'approve', display: 'Retry product processing' }] : [])
    ];
  }
  return [];
};

export const isLiveMatchActionable = row => getLiveMatchActions(row).length > 0;

// Find the human confirmation of this run in the loaded review history.
export const getLiveMatchConfirmation = row => {
  if (!row?.runId) return null;
  return (row.reviews || []).find(review => _.includes(review.tags, 'human_match_audit') && _.includes(review.tags, `audited_run_id:${row.runId}`)) || null;
};

// Offer a human audit only for an unchanged automatic match without a saved confirmation.
export const canConfirmAutomaticMatch = row => Boolean(row && row.status === 'SUCCEEDED' && ['sibling', 'new_product'].includes(row.agentMatchVerdict || row.matchVerdict) &&
  row.applyLane === 'live' && row.applyDecision === 'applied' &&
  row.appliedAt && row.appliedBy_User_id === null && !row.revertedAt && row.hasRevertFootprint === false && !row.applySkipReason &&
  !row.humanAction && !row.humanResolvedAt && !row.reviewer_User_id && row.outcomeAgreement == null && row.runId && row.applied_Product_id &&
  !getLiveMatchConfirmation(row));

export const getLiveDateParams = date => {
  const day = date && moment(date, 'YYYY-MM-DD', true);
  if (!day || !day.isValid()) return {};
  return { from: day.clone().startOf('day').toISOString(), to: day.clone().endOf('day').toISOString(), label: day.format('MMM D, YYYY') };
};

export const getLiveActivityGroups = rows =>
  Object.entries(_.groupBy(rows, row => moment(row.createdAt).format('YYYY-MM-DD'))).map(([date, items]) => ({
    date,
    label: moment(date).calendar(null, { sameDay: '[Today] · MMM D', lastDay: '[Yesterday] · MMM D', lastWeek: 'ddd · MMM D', sameElse: 'MMM D, YYYY' }),
    rows: items
  }));

// Why the agent decided what it decided, in one line: the match agent's own reasoning when the
// run produced one, otherwise the decision summary's detail (triage/blocked reason, failed run,
// …). The list payload flattens it to agentMatchReasoning; the detail payload carries the run JSON.
export const getLiveMatchReason = row => {
  const raw = row?.agentMatchReasoning ?? row?.agentRunJson?.agentResults?.match?.reasoning;
  const reasoning = typeof raw === 'string' ? raw.trim() : '';
  if (reasoning) return reasoning;
  return getAgentDecisionSummary(row).detail || null;
};

// Shapes the header exactly like a loaded summary so the strip can reserve its height before the
// first response lands.
export const EMPTY_LIVE_SUMMARY = { autoApplied: 0, approved: 0, open: 0, running: 0, sentToHumans: 0 };

export const getLiveDayStats = summary => {
  return [
    { key: 'auto', label: 'Completed automatically', value: summary.autoApplied ?? '—' },
    { key: 'approved', label: 'Approved by your team', value: summary.approved ?? '—' },
    summary.corrected > 0 && { key: 'corrected', label: 'Product chosen by your team', value: summary.corrected },
    { key: 'pending', label: 'Waiting to apply', value: summary.open ?? '—' },
    { key: 'running', label: 'Still processing', value: summary.running ?? '—' },
    { key: 'passed', label: 'Sent to catalog team', value: summary.sentToHumans ?? '—' },
    summary.alreadyResolved > 0 && { key: 'already_resolved', label: 'Already processed', value: summary.alreadyResolved },
    summary.creationRequests?.links > 0 && {
      key: 'creation_requests',
      label: 'Create requests',
      value: summary.creationRequests.links,
      detail: `${getPrettyPriceDisplay(summary.creationRequests.costUsd, { precision: 2 })} spent`
    }
  ].filter(Boolean);
};

/* ────────────────────────────────────────────────────────────────
 * Row facts a reviewer judges without clicking
 * ─────────────────────────────────────────────────────────────── */

// Mirror of SEND_BACK_REASONS (packages/server/src/pin_match/silent_eval_decision_helpers.js) — the
// admin app has no @shopmy/server dependency, and the send-to-human endpoint re-validates the reason.
export const SEND_BACK_REASONS = ['wrong_product', 'variant_confusion', 'wrong_brand', 'preowned_or_marketplace', 'insufficient_evidence'];
export const SEND_BACK_REASON_OPTIONS = SEND_BACK_REASONS.map(tag => ({ value: tag, label: getReviewTagLabel(tag) }));

export const getSendBackBody = (reason, note = '') => {
  const trimmed = note.trim();
  return trimmed ? { reason, note: trimmed } : { reason };
};

// The URL Data page deep-links by the hex hash of the stem, which the list payload returns as `hash`.
export const getUrlDataAdminUrl = row => {
  const hash = row?.hash || row?.hashedUrlStem;
  return hash ? `/url-data?hashedUrlStem=${encodeURIComponent(hash)}` : null;
};

export const getProductAdminUrl = Product_id => `/products/${Product_id}`;

export const formatPrice = (amount, currency) => {
  if (amount == null || amount === '' || Number.isNaN(Number(amount))) return null;
  const code = String(currency || 'USD').toUpperCase();
  try {
    return formatAmountInCurrency(Number(amount), code);
  } catch {
    return `${Number(amount).toFixed(2)} ${code}`;
  }
};

export const isPreownedSource = row => isTrue(row?.isPreowned);

const EXCLUDED_RESALE_DISMISSALS = {
  excluded_platform: { label: 'Suggested dismissal: resale site the catalog excludes', action: 'Dismiss excluded resale listing' },
  used_etsy_listing: { label: 'Suggested dismissal: used or vintage Etsy listing', action: 'Dismiss used or vintage listing' }
};
const getExcludedResaleDismissal = row => EXCLUDED_RESALE_DISMISSALS[row?.currentApplyPolicy?.excludedResaleListing?.reason];

export const isNonProductDismissal = row => row?.applyAction === 'dismiss_url' || row?.appliedAction === 'dismiss_url' || row?.appliedChangesJson?.action === 'dismiss_url';

export const isProductCreation = row => (row?.agentMatchVerdict || row?.matchVerdict || row?.agentRunJson?.agentResults?.match?.decision?.verdict) === 'new_product';

// The apply created the product; a creation whose URL another product took mid-run applies as an attach instead.
export const isProductCreated = row => Boolean(row?.createdProductId);

export const isCatalogBrandCreation = row => Boolean(row?.createdAllBrandId) ||
  (row?.applyDecision === 'proposed' && row.applyAction === 'create_product' && row.currentApplyPolicy?.createsCatalogBrand === true);

export const isProductCreationProcessingPending = row => {
  if (row?.revertedAt) return false;
  if (isTrue(row?.lifecycleProcessingRequired)) return !row.lifecycleProcessingCompletedAt && !row.lifecycleProcessingCancelledAt;
  return Boolean(row?.createdProductId && (!row.creationProcessingQueuedAt || !row.matchProcessingQueuedAt || (row.createdAllBrandId && !row.brandCreationProcessingQueuedAt)));
};

export const isAutoApplied = row => Boolean(row?.appliedAt) && row?.appliedBy_User_id == null;

export const getLiveMatchProduct = row =>
  row?.applyDecision === 'proposed' || (row?.applyDecision === 'applied' && !row?.revertedAt)
    ? row?.appliedProduct || row?.agentProduct || row?.urlTakenByProduct || null
    : row?.humanAction ? row.humanProduct || null : row?.agentProduct || null;

// The moment the row reached its current state: undone, applied, or proposed.
export const getLiveMatchTimestamp = row => row?.revertedAt || row?.appliedAt || row?.createdAt || null;

export const getLiveMatchConfidence = row => {
  if (isRunningProposal(row)) return null;
  if (row?.currentApplyPolicy) return Number.isFinite(row.currentApplyPolicy.confidence) ? row.currentApplyPolicy.confidence : null;
  if (getLiveMatchReviewReason(row)) return null;
  const value = row?.applyConfidence ?? row?.overallConfidence;
  return value == null || value === '' || Number.isNaN(Number(value)) ? null : Number(value);
};

export const getLiveMatchConfidenceBand = confidence => (confidence == null ? null : confidence >= 0.95 ? 'high' : confidence >= 0.85 ? 'mid' : 'low');

export const formatConfidence = confidence => (confidence == null ? '—' : `${Number((confidence * 100).toFixed(2))}%`);

export const isLiveMatchBrandMismatch = row =>
  isBrandMismatch(getLambdaSummary(row).brandName, getLiveMatchProduct(row)?.brand || row?.currentApplyPolicy?.productToCreateBrand || getAgentSummary(row).brandName);

// Mirrors normalizeBrandNameToken, conjunctionFreeBrandToken and tokensContain in packages/server/src/pin_match/source_manufacturer.js.
const normalizeBrandNameToken = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\.(?:com|net|org|co|io|us|uk|ca|au|de|fr|it|es|jp)(?:\.[a-z]{2})?\s*$/, '')
    .replace(/&/g, ' and ')
    .replace(/\b(?:by|the)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '');

const conjunctionFreeBrandToken = value => normalizeBrandNameToken(String(value || '').replace(/&|\band\b/gi, ' '));

const tokensAlign = (a, b) => Boolean(a && b) && (a === b || (a.length >= 3 && b.length >= 3 && (a.includes(b) || b.includes(a))));

export const isBrandMismatch = (sourceBrand, productBrand) => {
  const source = normalizeBrandNameToken(sourceBrand);
  const product = normalizeBrandNameToken(productBrand);
  if (!source || !product) return false;
  return !tokensAlign(source, product) && !tokensAlign(conjunctionFreeBrandToken(sourceBrand), conjunctionFreeBrandToken(productBrand));
};

/* ────────────────────────────────────────────────────────────────
 * Client-side sort + filter over the loaded lists
 * ─────────────────────────────────────────────────────────────── */

export const LIVE_MATCH_SORT_OPTIONS = [
  { value: 'confidence_desc', label: 'Confidence ↓' },
  { value: 'confidence_asc', label: 'Confidence ↑' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' }
];

const getTimestampValue = row => (getLiveMatchTimestamp(row) ? moment(getLiveMatchTimestamp(row)).valueOf() : 0);

// Unscored rows sort last in either confidence direction.
const SORT_ORDERS = {
  confidence_desc: [row => getLiveMatchConfidence(row) ?? -1, 'desc'],
  confidence_asc: [row => getLiveMatchConfidence(row) ?? 2, 'asc'],
  newest: [getTimestampValue, 'desc'],
  oldest: [getTimestampValue, 'asc']
};

export const sortLiveMatchRows = (rows, sort) => {
  const [iteratee, order] = SORT_ORDERS[sort] || SORT_ORDERS.newest;
  return _.orderBy(rows || [], [iteratee], [order]);
};

// The list controls live in the query string under short names, so a refresh or a shared link
// shows the same list. Values equal to the mode's default stay out of the URL.
const CONTROL_PARAMS = [
  ['sort', 'sort'],
  ['date', 'date'],
  ['state', 'state'],
  ['minConfidence', 'conf'],
  ['domain', 'domain'],
  ['preownedOnly', 'preowned'],
  ['mismatchOnly', 'mismatch']
];

export const readControlsFromSearch = (search, defaults) => {
  const params = new URLSearchParams(search);
  return CONTROL_PARAMS.reduce((controls, [key, param]) => {
    if (!(key in defaults) || params.get(param) == null) return controls;
    const value = parseSearchParam(params.get(param), defaults[key]);
    if (key === 'date' && !moment(value, 'YYYY-MM-DD', true).isValid()) return controls;
    if (key === 'state' && !LIVE_ACTIVITY_STATE_OPTIONS.some(option => option.value === value)) return controls;
    return { ...controls, [key]: value };
  }, { ...defaults });
};

export const writeControlsToSearch = (search, controls, defaults) => {
  const params = new URLSearchParams(search);
  CONTROL_PARAMS.forEach(([key, param]) => {
    const value = controls[key];
    if (value == null || value === '' || value === defaults[key]) params.delete(param);
    else params.set(param, value === true ? '1' : String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const filterLiveMatchRows = (rows, { minConfidence = 0, domain = '', preownedOnly = false, mismatchOnly = false, state = 'all' } = {}) => {
  const needle = domain.trim().toLowerCase();
  return (rows || []).filter(
    row =>
      (!minConfidence || (getLiveMatchConfidence(row) ?? 0) >= minConfidence) &&
      (!needle || String(row.domain || '').toLowerCase().includes(needle)) &&
      (!preownedOnly || isPreownedSource(row)) &&
      (!mismatchOnly || isLiveMatchBrandMismatch(row)) &&
      (state === 'all' || getApplyDecisionSummary(row).key === state)
  );
};

/* ────────────────────────────────────────────────────────────────
 * Detail drawer
 * ─────────────────────────────────────────────────────────────── */

// The candidates the agent weighed, in the order it saw them, with the one it picked marked. The
// run JSON carries no images; the detail payload's candidateProducts hydrate title, brand and image.
export const getLiveMatchCandidates = evaluation => {
  const chosenId = Number(evaluation?.agentProduct?.id || getAgentSummary(evaluation).Product_id || evaluation?.appliedProduct?.id);
  const decision = evaluation?.agentRunJson?.agentResults?.match?.decision || {};
  const similarById = _.keyBy(decision.topSimilarProducts || [], 'Product_id');
  const candidates = _.uniqBy([...(decision.closeProductIds || []).map(Product_id => ({ Product_id, ...similarById[Product_id] })), ...(decision.topSimilarProducts || [])], 'Product_id');
  const previewById = _.keyBy(evaluation?.candidateProducts || [], 'id');
  return candidates.map(candidate => {
    const preview = previewById[candidate.Product_id] || {};
    return {
      Product_id: candidate.Product_id,
      title: preview.title || candidate.title || `Product #${candidate.Product_id}`,
      brand: preview.brand || candidate.brand || null,
      image: preview.image || null,
      score: candidate.score == null ? null : Number(candidate.score).toFixed(2),
      isUsed: Boolean(preview.isUsed ?? candidate.isUsed),
      chosen: Number(candidate.Product_id) === chosenId
    };
  });
};

// The quiet footer of the detail drawer: who and what produced this row, only the facts that exist.
export const getLiveMatchRunFacts = evaluation => {
  if (!evaluation) return [];
  const run = evaluation.agentRunJson || {};
  const rulesetVersion = evaluation.rulesetVersion || run.rulesetVersion;
  const modelVersions = evaluation.modelVersions || run.modelVersions;
  const review = evaluation.latestReview;
  const reviewLine =
    review &&
    [review.reviewer?.name, ...(review.tags || []).map(getReviewTagLabel), review.notes ? `“${review.notes}”` : null].filter(Boolean).join(' · ');
  const models = modelVersions && _.uniq(Object.values(_.omit(modelVersions, 'aggregator'))).join(', ');
  return [
    { label: 'run', value: evaluation.runId },
    { label: 'ruleset', value: rulesetVersion },
    { label: 'model', value: models },
    { label: 'cost', value: evaluation.costUsd == null ? null : `$${Number(evaluation.costUsd).toFixed(3)}` },
    { label: 'latency', value: evaluation.latencyMs == null ? null : `${Math.round(Number(evaluation.latencyMs) / 100) / 10}s` },
    { label: 'captured', value: evaluation.createdAt && formatDate(evaluation.createdAt) },
    { label: 'applied', value: evaluation.appliedAt && formatDate(evaluation.appliedAt) },
    { label: 'undone', value: evaluation.revertedAt && formatDate(evaluation.revertedAt) },
    { label: 'review', value: reviewLine }
  ].filter(fact => fact.value);
};

/* ────────────────────────────────────────────────────────────────
 * Bulk actions over selected proposals
 * ─────────────────────────────────────────────────────────────── */

// Bounded so a 200-row "select all" doesn't fire 200 concurrent writes at the server; each apply
// cascades into catalog/queue updates, so a small cap keeps the DB happy without being slow.
export const BULK_CONCURRENCY = 4;

/**
 * Runs `action(row)` for every row with bounded concurrency. Never throws: each row settles to
 * `{ row, ok }` or `{ row, ok: false, error }`, in input order. The server stays the authority —
 * callers pass the same single-row endpoints the row buttons use, so each row is re-validated
 * server-side and a race (e.g. a human matched the URL mid-batch) fails that row alone.
 */
export const runBulkAction = async (rows, action, { concurrency = BULK_CONCURRENCY, onProgress } = {}) => {
  const results = new Array(rows.length);
  let nextIndex = 0;
  let doneCount = 0;
  const worker = async () => {
    while (nextIndex < rows.length) {
      const index = nextIndex++;
      const row = rows[index];
      try {
        const response = await action(row);
        results[index] = { row, ok: true, alreadyMatched: Boolean(response?.alreadyMatched), recoveryQueued: Boolean(response?.recoveryQueued) };
      } catch (error) {
        results[index] = { row, ok: false, error };
      }
      doneCount += 1;
      if (onProgress) onProgress(doneCount, rows.length);
    }
  };
  await Promise.all(_.times(_.clamp(concurrency, 1, rows.length), worker));
  return results;
};

// One line for the toast. The API layer rejects with plain strings, so read both shapes.
export const getBulkResultMessage = (results, pastTenseVerb) => {
  const failures = results.filter(result => !result.ok);
  const okCount = results.length - failures.length;
  const alreadyMatched = results.filter(result => result.alreadyMatched).length;
  const recovering = results.filter(result => result.recoveryQueued).length;
  const matchesWord = count => (count === 1 ? 'match' : 'matches');
  const firstError = failures.length ? String(failures[0].error?.message || failures[0].error || 'unknown error') : null;
  if (failures.length && !recovering && !alreadyMatched) {
    return { ok: false, message: `${pastTenseVerb} ${okCount} of ${results.length} ${matchesWord(results.length)} — ${failures.length} failed (${firstError})` };
  }
  const applied = okCount - alreadyMatched - recovering;
  const clauses = [`${pastTenseVerb} ${applied} ${matchesWord(applied)}.`];
  if (recovering) clauses.push(`${recovering} being investigated automatically.`);
  if (alreadyMatched) clauses.push(`${alreadyMatched} already linked to the suggested product.`);
  if (failures.length) clauses.push(`${failures.length} failed (${firstError}).`);
  return { ok: !failures.length, message: clauses.join(' ') };
};
